import { useMemo, useState } from "react";
import { arrayOf, func, number, shape, string } from "prop-types";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";
import { Button, InfoLabel, Autocomplete, ControlledRadioGroup } from "@components";
import { CircularProgress, Divider, Grid, Link } from "@material-ui/core";


const newProjectFormSchema = (t) =>
  yup.object().shape({
    project: yup.object()
      .shape({
        name: yup.string(),
        id: yup
          .string()
          .required(t("views.bt.select_project")),
      })
      .required(t("views.bt.select_project")),
    workspace: yup.object()
      .shape({
        name: yup.string(),
        id: yup
          .string()
          .required(t("views.bt.select_workspace")),
      })
      .required(t("views.bt.select_workspace")),
    version: yup
      .number()
      .required(t("errors.version_key.required")),
  });

export const JiraExportForm = ({
  onSubmit,
  projects,
  workspaces,
  onWorkspaceSelect,
  onConnectNewJiraWorkspace,
  projectVersions,
  // defaultProject,
  defaultWorkspace,
  defaultVersion
}) => {
  const { t } = useTranslation();
  
  const defaultValues = useMemo(() => ({
    project: /*defaultProject || */{
      name: null,
      id: null
    },
    workspace: defaultWorkspace || {
      name: null,
      id: null
    },
    version: defaultVersion.toString(),
  }), []);
  
  const {
    control,
    handleSubmit,
    reset: resetForm,
    setError,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(newProjectFormSchema(t)),
    defaultValues,
  });
  
  const [projectsLoading, setProjectsLoading] = useState(false);
  
  const submitHandler = async (data) => {
    const result = await onSubmit(data);
    
    if (result) {
      const { errors, reset } = result;
      
      if (reset) {
        resetForm(defaultValues);
        return;
      }
      
      errors?.forEach(({ field, message }) => {
        setError(field, { message });
      });
    }
  };
  
  return (
    <form
      autoComplete="on"
      aria-label="companyDetails form"
      onSubmit={handleSubmit(submitHandler)}
    >
      <Grid item container direction="column" wrap="nowrap" className="p-6">
        <InfoLabel
          spacing={8}
          label="views.bt.select_workspace_full"
          titleAction={projectsLoading &&
            <CircularProgress
              aria-label="progress indicator"
              size={24}
              color="inherit"
            />
          }
          />
        <Controller
          name="workspace"
          control={control}
          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
            <Autocomplete
              disabled={projectsLoading}
              disableClearable
              value={value?.id}
              options={workspaces}
              name={name}
              idKey="id"
              labelKey="name"
              inputLabel={t("common.workspace")}
              fullWidth
              onChange={async (_, v) => {
                onChange(v);
                setProjectsLoading(true);
                await onWorkspaceSelect(v);
                setProjectsLoading(false);
              }}
              error={!!error?.id}
              helperText={error?.id?.message}
              size="small"
            />
          )}
        />
        <Link onClick={onConnectNewJiraWorkspace} className="cursor-pointer -mt-2 mb-2">
          {t("views.bt.connect_new_workspace")}
        </Link>
        
        <InfoLabel
          spacing={8}
          label="views.bt.select_project_full"
          titleAction={projectsLoading &&
            <CircularProgress
              aria-label="progress indicator"
              size={24}
              color="inherit"
            />
          }
        />
        <Controller
          name="project"
          control={control}
          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
            <Autocomplete
              disabled={projectsLoading}
              disableClearable
              value={value?.id}
              options={projects}
              name={name}
              idKey="id"
              labelKey="name"
              inputLabel={t("common.project")}
              fullWidth
              onChange={(_, v) => onChange(v)}
              error={!!error?.id}
              helperText={error?.id?.message}
              size="small"
            />
          )}
        />
        
        <InfoLabel spacing={8} label="views.bt.following_version" />
        <ControlledRadioGroup
          control={control}
          name="version"
          options={projectVersions.map((v, index) => ({
            value: v.key, label: `${t("common.version")} ${index + 1}: ${v.name}`
          }))}
        />
        
        <Divider className="mt-2 -mx-6 mb-3" />
        
        <Grid item container justifyContent="flex-end">
          <Button
            isSubmit
            name="submit"
            variant="contained"
            endIcon={
              isSubmitting ? (
                <CircularProgress
                  aria-label="progress indicator"
                  size={24}
                  color="inherit"
                />
              ) : undefined
            }
          >
            {t("common.next")}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

JiraExportForm.propTypes = {
  onSubmit: func,
  projectVersions: arrayOf(shape({
    key: number,
    name: string
  })),
  projects: arrayOf(shape({
    id: string.isRequired,
    name: string.isRequired,
    // disabled: bool,
  })),
  workspaces: arrayOf(shape({
    id: string.isRequired,
    name: string.isRequired,
    // disabled: bool,
  })),
  defaultVersion: number.isRequired,
  // defaultProject: shape({
  //   name: string.isRequired,
  //   key: string.isRequired,
  // }),
  defaultWorkspace: shape({
    name: string.isRequired,
    key: string.isRequired,
  }),
  onWorkspaceSelect: func.isRequired,
  onConnectNewJiraWorkspace: func.isRequired,
};